import React from 'react';
import moment from 'moment-jalaali';
import { endOfDate } from 'utils/date';
import { englishNumber } from 'utils/number';
import { BaseDatePickerContainer, Text, DatePickerHeader } from './styles';
import Tabs from 'components/Tabs';
import { TabItems } from 'components/Tabs/Tabs';
import ButtonGroup from 'components/ButtonGroup';
import YearPicker from 'components/YearPicker';
import { Calendar } from 'react-multi-date-picker';
import i18n, { getCalendarLocal } from '../../i18n';
import { getMultiDateLanguageConfigurationBaseOnLang } from '../MyDatePicker/MultiDatePicker';

moment.loadPersian({ dialect: 'persian-modern' });

type DateScale = 'month' | 'season' | 'date';

export type ILocal = 'en' | 'fa_IR@calendar=persian';
const faLang = 'fa_IR@calendar=persian';

type ComponentProps = {
  width?: string;
  baseDate: Date;
  onChangeBaseDate: (date: Date) => void;
  sendDateOnTabChange?: boolean;
  showDaySelect?: boolean;
  local?: ILocal;
};

type ComponentStates = {
  dateIndex: number;
  dateScale: DateScale;
  year: number;
  selectedDate: Date;
};

// Todo: checkout the reason of its usage
const seasons = {
  1: '-02-15',
  2: '-05-15',
  3: '-08-15',
  4: '-11-15',
};

const enSeasons = {
  1: '-02-05',
  2: '-05-05',
  3: '-08-05',
  4: '-11-05',
};

export const monthItems = (lng: 'fa' | 'en') => ({
  1: i18n.t('1stMonth', { ns: 'survey', lng: lng }),
  2: i18n.t('2ndMonth', { ns: 'survey', lng: lng }),
  3: i18n.t('3rdMonth', { ns: 'survey', lng: lng }),
  4: i18n.t('4thMonth', { ns: 'survey', lng: lng }),
  5: i18n.t('5thMonth', { ns: 'survey', lng: lng }),
  6: i18n.t('6thMonth', { ns: 'survey', lng: lng }),
  7: i18n.t('7thMonth', { ns: 'survey', lng: lng }),
  8: i18n.t('8thMonth', { ns: 'survey', lng: lng }),
  9: i18n.t('9thMonth', { ns: 'survey', lng: lng }),
  10: i18n.t('10thMonth', { ns: 'survey', lng: lng }),
  11: i18n.t('11thMonth', { ns: 'survey', lng: lng }),
  12: i18n.t('12thMonth', { ns: 'survey', lng: lng }),
});

const seasonItems = {
  1: i18n.t('untilTheEndOfSpring', { ns: 'survey', lng: 'fa' }),
  2: i18n.t('untilTheEndOfSummer', { ns: 'survey', lng: 'fa' }),
  3: i18n.t('untilTheEndOfAutumn', { ns: 'survey', lng: 'fa' }),
  4: i18n.t('untilTheEndOfWinter', { ns: 'survey', lng: 'fa' }),
};

const enSeasonItems = {
  1: i18n.t('untilTheEndOfWinter', { ns: 'survey' }),
  2: i18n.t('untilTheEndOfSpring', { ns: 'survey' }),
  3: i18n.t('untilTheEndOfSummer', { ns: 'survey' }),
  4: i18n.t('untilTheEndOfAutumn', { ns: 'survey' }),
};

// const thisMonth = englishNumber(moment().format('jM'));
// const thisYear = englishNumber(moment().format('jYYYY'));

class BaseDatePicker extends React.PureComponent<
  ComponentProps,
  ComponentStates
> {
  state: ComponentStates = {
    dateIndex:
      this.props.local === faLang
        ? Number(englishNumber(moment(this.props.baseDate).format('jM')))
        : Number(moment(this.props.baseDate).locale('en').format('M')),
    dateScale: 'month',
    year:
      this.props.local === faLang
        ? Number(englishNumber(moment(this.props.baseDate).format('jYYYY')))
        : Number(moment(this.props.baseDate).locale('en').format('YYYY')),
    selectedDate: new Date(),
  };

  calculateDate = (year: number, dateScale: DateScale, dateIndex: number) => {
    const lang = this.props.local || getCalendarLocal();
    if (dateScale === 'month') {
      if (lang === faLang) {
        const date = endOfDate(
          moment(`${year}-${Number(dateIndex)}`, 'jYYYY-jM').toDate(),
          'month'
        );
        this.props.onChangeBaseDate(date);
      } else if (lang === 'en') {
        const date = endOfDate(
          moment(`${year}-${Number(dateIndex)}`, 'YYYY-M')
            .locale('en')
            .toDate(),
          'month'
        );
        this.props.onChangeBaseDate(date);
      }
    } else if (dateScale === 'season') {
      if (lang === faLang) {
        const seasonDate = seasons[dateIndex];
        const date = endOfDate(
          moment(`${year}${seasonDate}`, 'jYYYY-jMM-jDD').toDate(),
          'season'
        );
        this.props.onChangeBaseDate(date);
      } else if (lang === 'en') {
        const seasonDate = enSeasons[dateIndex];
        const date = endOfDate(
          moment(`${year}${seasonDate}`, 'YYYY-MM-DD').locale('en').toDate(),
          'season'
        );
        this.props.onChangeBaseDate(date);
      }
    } else {
      this.props?.onChangeBaseDate?.(this.state?.selectedDate as Date);
    }
  };

  handleChangeTab = (dateScale: string | number) => {
    this.setState({ dateScale: dateScale as DateScale });
    const { year, dateIndex } = this.state;
    if (this.props.sendDateOnTabChange)
      this.calculateDate(year, dateScale as DateScale, dateIndex);
  };

  handleChangeDateKey = (dateIndex: string) => {
    this.setState({ dateIndex: Number(dateIndex) });
    const { year, dateScale } = this.state;
    this.calculateDate(year, dateScale, Number(dateIndex));
  };

  handleChangeYear = (year: number) => {
    this.setState({ year });
    const { dateScale, dateIndex } = this.state;
    this.calculateDate(year, dateScale, dateIndex);
  };

  handleChangeTargetDate = (newDate: Date) => {
    this.setState(
      (prevState) => {
        return {
          ...prevState,
          selectedDate: newDate,
        };
      },
      () => {
        this.calculateDate(0, 'date', 0);
      }
    );
  };

  render() {
    const { year, dateIndex, dateScale } = this.state;
    const conf = getMultiDateLanguageConfigurationBaseOnLang(this.props.local);
    const MonthSelector = (
      <ButtonGroup
        items={monthItems(this.props.local === faLang ? 'fa' : 'en')}
        onSelect={this.handleChangeDateKey}
        selected={dateIndex.toString()}
        columnsNumber={4}
        border={false}
      />
    );
    const SeasonSelector = (
      <ButtonGroup
        items={this.props.local === faLang ? seasonItems : enSeasonItems}
        onSelect={this.handleChangeDateKey}
        selected={dateIndex.toString()}
      />
    );
    const dateSelector = (
      <Calendar
        calendar={conf.calendar}
        locale={conf.locale}
        format={'YYYY/MM/DD'}
        value={this.state.selectedDate}
        onChange={(dateObjects) => {
          this.handleChangeTargetDate?.(dateObjects?.toDate() as Date);
        }}
        className="w-full shadow-none flex justify-center items-center"
      />
    );
    const tabItems: TabItems = {
      date: {
        title: i18n.t('day', { ns: 'survey' }),
        content: dateSelector,
      },
      month: {
        title: i18n.t('month', { ns: 'survey' }),
        content: MonthSelector,
      },
      season: {
        title: i18n.t('season', { ns: 'survey' }),
        content: SeasonSelector,
      },
    };

    if (!this.props.showDaySelect) {
      delete tabItems.date;
    }
    return (
      <BaseDatePickerContainer width={this.props.width}>
        <DatePickerHeader>
          <Text>{i18n.t('baseDate', { ns: 'survey' }) as string}</Text>
          {dateScale !== 'date' && (
            <YearPicker year={year} onChange={this.handleChangeYear} />
          )}
        </DatePickerHeader>
        <Tabs
          tabItems={tabItems}
          activeTab={dateScale}
          onChangeActiveTab={this.handleChangeTab}
        />
      </BaseDatePickerContainer>
    );
  }
}

export default BaseDatePicker;
